import { ApplicationShell as TheiaApplicationShell } from '@theia/core/lib/browser';
import { TheiaDockPanel } from '@theia/core/lib/browser/shell/theia-dock-panel';
import { TheiaSplitPanel } from '@theia/core/lib/browser/shell/theia-split-panel';
import { Emitter, Event } from '@theia/core/lib/common';
import { SplitLayout, type Layout } from '@theia/core/shared/@lumino/widgets';
import { injectable, postConstruct, type interfaces } from '@theia/core/shared/inversify';

type LayoutData = TheiaApplicationShell.LayoutData & {
    lrPanelSizes?: number[];
    mainRightPanelSizes?: number[];
    mainBottomPanelSizes?: number[];
    /** @deprecated migrated to mainBottomPanelSizes */
    rightBottomPanelSizes?: number[];
};

@injectable()
export class AppShell extends TheiaApplicationShell {
    private static readonly DEFAULT_BOTTOM_PANEL_RATIO = 0.22;
    private static readonly MAX_BOTTOM_PANEL_RATIO = 0.35;
    private static readonly MIN_LEFT_PANEL_WIDTH = 240;
    /** Horizontal split: left | (main+right+bottom). */
    protected lrPanel!: TheiaSplitPanel; // horizontal: [left | mainBottom]
    /** Horizontal split: main | right. */
    protected mainRightPanel!: TheiaSplitPanel; // horizontal: [main | right]
    /** Vertical split: (main+right) over bottom. */
    protected mainBottomPanel!: TheiaSplitPanel; // vertical:   [mainRight / bottom]

    private readonly _onDidToggleBottomPanelEmitter = new Emitter<void>();
    readonly onDidToggleBottomPanel: Event<void> = this._onDidToggleBottomPanelEmitter.event;

    private _pendingLrPanelSizes: number[] | undefined;
    private _pendingMainRightPanelSizes: number[] | undefined;
    private _pendingMainBottomPanelSizes: number[] | undefined;

    @postConstruct()
    protected override init(): void {
        super.init();
        this.restrictCrossPanelDragging();
        // Auto-expand when a tab is clicked while the panel is collapsed.
        this.bottomPanel.onDidChangeCurrent(() => {
            if (this._bottomCollapsed) {
                this._expandBottomPanel();
            }
        });
    }

    override getLayoutData(): TheiaApplicationShell.LayoutData {
        const data = super.getLayoutData() as LayoutData;
        if (this.lrPanel) {
            data.lrPanelSizes = this.lrPanel.relativeSizes();
        }
        if (this.mainRightPanel) {
            data.mainRightPanelSizes = this.mainRightPanel.relativeSizes();
        }
        if (this.mainBottomPanel) {
            data.mainBottomPanelSizes = this.mainBottomPanel.relativeSizes();
        }
        return data;
    }

    override async setLayoutData(layoutData: TheiaApplicationShell.LayoutData): Promise<void> {
        await super.setLayoutData(layoutData);
        const lvglData = layoutData as LayoutData;
        if (lvglData.lrPanelSizes?.length === 3) {
            const [left, main, rightColumn] = lvglData.lrPanelSizes;
            this._pendingLrPanelSizes = [left, main + rightColumn];
            this._pendingMainRightPanelSizes = [main, rightColumn];
        } else if (lvglData.lrPanelSizes?.length) {
            this._pendingLrPanelSizes = lvglData.lrPanelSizes;
        }
        if (lvglData.mainRightPanelSizes?.length) {
            this._pendingMainRightPanelSizes = lvglData.mainRightPanelSizes;
        }
        const bottomSizes = lvglData.mainBottomPanelSizes ?? lvglData.rightBottomPanelSizes;
        if (bottomSizes?.length) {
            this._pendingMainBottomPanelSizes = bottomSizes;
        }
    }

    applyPanelSizes(): void {
        requestAnimationFrame(() => {
            if (this.lrPanel) {
                if (this._pendingLrPanelSizes) {
                    this.lrPanel.setRelativeSizes(this._pendingLrPanelSizes);
                    this._pendingLrPanelSizes = undefined;
                } else {
                    const total = this.lrPanel.node.offsetWidth;
                    const remaining = total - 280;
                    if (remaining > 0) {
                        this.lrPanel.setRelativeSizes([280, remaining]);
                    }
                }
            }
            if (this.mainRightPanel) {
                if (this._pendingMainRightPanelSizes) {
                    this.mainRightPanel.setRelativeSizes(this._pendingMainRightPanelSizes);
                    this._pendingMainRightPanelSizes = undefined;
                } else {
                    const total = this.mainRightPanel.node.offsetWidth;
                    if (total > 0) {
                        this.mainRightPanel.setRelativeSizes([total / 2, total / 2]);
                    }
                }
            }
            if (this.mainBottomPanel && this._pendingMainBottomPanelSizes) {
                this.mainBottomPanel.setRelativeSizes(
                    this.clampMainBottomPanelSizes(this._pendingMainBottomPanelSizes)
                );
                this._pendingMainBottomPanelSizes = undefined;
            } else if (this.mainBottomPanel) {
                const totalHeight = this.mainBottomPanel.node.offsetHeight;
                if (totalHeight > 0) {
                    const bottomHeight = Math.max(
                        140,
                        Math.min(totalHeight * AppShell.DEFAULT_BOTTOM_PANEL_RATIO, totalHeight * 0.5)
                    );
                    const topHeight = Math.max(1, totalHeight - bottomHeight);
                    this.mainBottomPanel.setRelativeSizes([topHeight, bottomHeight]);
                }
            }
        });
    }

    protected clampMainBottomPanelSizes(sizes: number[]): number[] {
        if (sizes.length !== 2) return sizes;
        const total = sizes[0] + sizes[1];
        if (total <= 0) return sizes;
        const bottomRatio = sizes[1] / total;
        if (bottomRatio <= AppShell.MAX_BOTTOM_PANEL_RATIO) return sizes;
        const clampedBottom = total * AppShell.MAX_BOTTOM_PANEL_RATIO;
        return [total - clampedBottom, clampedBottom];
    }

    protected override createLayout(): Layout {
        const mainRightLayout = this.createSplitLayout([this.mainPanel, this.rightPanelHandler.container], [1, 0], {
            orientation: 'horizontal',
            spacing: 0
        });
        this.mainRightPanel = new TheiaSplitPanel({ layout: mainRightLayout });
        this.mainRightPanel.id = 'theia-main-right-split-panel';

        const mainBottomLayout = this.createSplitLayout([this.mainRightPanel, this.bottomPanel], [1, 0], {
            orientation: 'vertical',
            spacing: 0
        });
        this.mainBottomPanel = new TheiaSplitPanel({ layout: mainBottomLayout });
        this.mainBottomPanel.id = 'theia-main-bottom-split-panel';

        const lrLayout = this.createSplitLayout([this.leftPanelHandler.container, this.mainBottomPanel], [0, 1], {
            orientation: 'horizontal',
            spacing: 0
        });
        this.lrPanel = new TheiaSplitPanel({ layout: lrLayout });
        this.lrPanel.id = 'theia-left-right-split-panel';
        this.clampLeftPanelHandle(this.lrPanel);

        return this.createBoxLayout([this.topPanel, this.lrPanel, this.statusBar], [0, 1, 0], {
            direction: 'top-to-bottom',
            spacing: 0
        });
    }

    setMainPanelVisible(visible: boolean): void {
        if (visible === this._mainPanelExpanded) return;
        this._mainPanelExpanded = visible;
        this._applyMainPanelSizes(visible);
    }

    isMainPanelVisible(): boolean {
        return this._mainPanelExpanded;
    }

    private _mainPanelExpanded = true;
    private _savedMainRightSizes: [number, number] | undefined;

    private _applyMainPanelSizes(visible: boolean): void {
        if (!this.mainRightPanel) return;

        this._setLeftHandleEnabled(false);

        if (visible) {
            this._stopDesignModeEnforcement();
            this._setMainRightHandleEnabled(true);
            const totalWidth = this.mainRightPanel.node.offsetWidth;
            const targetMainWidth = this._savedMainRightSizes?.[0] ?? totalWidth * 0.5;
            this._savedMainRightSizes = undefined;
            this._setMainPanelWidth(targetMainWidth);
        } else {
            this._savedMainRightSizes = [
                this.mainPanel.node.offsetWidth,
                this.rightPanelHandler.container.node.offsetWidth
            ];
            this._setMainRightHandleEnabled(true);
            this._setMainPanelWidth(0, () => {
                this._setMainRightHandleEnabled(false);
                this._startDesignModeEnforcement();
            });
        }
    }

    private _setMainPanelWidth(targetMainWidth: number, onDone?: () => void): void {
        if (!this.mainRightPanel) return;

        const layout = this.getSplitLayout(this.mainRightPanel);
        if (!layout) return;
        const handles = layout.handles;
        const totalWidth = this.mainRightPanel.node.offsetWidth;
        const clampedTargetMain = Math.max(0, Math.min(targetMainWidth, totalWidth));

        if (handles.length === 0) {
            this.mainRightPanel.setRelativeSizes([clampedTargetMain, Math.max(0, totalWidth - clampedTargetMain)]);
            this._completeMainPanelTransition(onDone);
            return;
        }

        layout.moveHandle(0, clampedTargetMain);
        this._completeMainPanelTransition(onDone);
    }

    private _completeMainPanelTransition(onDone?: () => void): void {
        this._setLeftHandleEnabled(true);
        onDone?.();
    }

    private clampLeftPanelHandle(panel: TheiaSplitPanel): void {
        const layout = this.getSplitLayout(panel);
        if (!layout) return;
        const originalMoveHandle = layout.moveHandle.bind(layout);
        layout.moveHandle = (index: number, position: number): void => {
            // Handle 0 is left<->mainBottom, clamp to minimum sidebar width.
            if (index === 0) {
                originalMoveHandle(index, Math.max(position, AppShell.MIN_LEFT_PANEL_WIDTH));
                return;
            }
            originalMoveHandle(index, position);
        };
    }

    private _setMainRightHandleEnabled(enabled: boolean): void {
        const layout = this.getSplitLayout(this.mainRightPanel);
        if (!layout) return;
        const h = layout.handles;
        if (h.length > 0) {
            h[0].style.pointerEvents = enabled ? '' : 'none';
            h[0].style.opacity = enabled ? '' : '0';
        }
    }

    private _setLeftHandleEnabled(enabled: boolean): void {
        const layout = this.getSplitLayout(this.lrPanel);
        if (!layout) return;
        const h = layout.handles;
        if (h.length > 0) {
            h[0].style.pointerEvents = enabled ? '' : 'none';
        }
    }

    private _designModeObserver: ResizeObserver | undefined;
    private _enforcingDesignLayout = false;

    private _startDesignModeEnforcement(): void {
        if (this._designModeObserver) return;
        this._designModeObserver = new ResizeObserver(() => {
            if (this._enforcingDesignLayout || this._mainPanelExpanded) return;
            const mainW = this.mainPanel.node.offsetWidth;
            if (mainW > 0 && this.mainRightPanel) {
                // Keep main at 0 while hidden; transfer width to right side.
                this._enforcingDesignLayout = true;
                const totalW = this.mainRightPanel.node.offsetWidth;
                this.mainRightPanel.setRelativeSizes([0, totalW]);
                requestAnimationFrame(() => {
                    this._enforcingDesignLayout = false;
                });
            }
        });
        this._designModeObserver.observe(this.mainPanel.node);
    }

    private _stopDesignModeEnforcement(): void {
        this._designModeObserver?.disconnect();
        this._designModeObserver = undefined;
    }

    private static readonly BOTTOM_TAB_BAR_H = 36;

    private _bottomCollapsed = false;
    private _savedBottomH: number | undefined;

    override async collapsePanel(area: TheiaApplicationShell.Area): Promise<void> {
        if (area === 'bottom') {
            this._collapseBottomPanel();
            return;
        }
        return super.collapsePanel(area);
    }

    override expandPanel(area: TheiaApplicationShell.Area): void {
        if (area === 'bottom') {
            this._expandBottomPanel();
            return;
        }
        super.expandPanel(area);
    }

    isBottomPanelCollapsed(): boolean {
        return this._bottomCollapsed;
    }

    private _collapseBottomPanel(): void {
        if (this._bottomCollapsed || !this.mainBottomPanel) return;
        this._bottomCollapsed = true;
        this._savedBottomH = this.bottomPanel.node.offsetHeight;
        this._setBottomResizeHandleEnabled(false);
        this._onDidToggleBottomPanelEmitter.fire();
        this._setBottomPanelSize(AppShell.BOTTOM_TAB_BAR_H);
    }

    private _expandBottomPanel(): void {
        if (!this._bottomCollapsed || !this.mainBottomPanel) return;
        this._bottomCollapsed = false;
        const targetSize = this._savedBottomH ?? Math.round(this.mainBottomPanel.node.offsetHeight * 0.35);
        this._setBottomResizeHandleEnabled(true);
        this._onDidToggleBottomPanelEmitter.fire();
        this._setBottomPanelSize(targetSize);
    }

    private _setBottomPanelSize(targetSize: number): void {
        if (!this.mainBottomPanel) return;

        const layout = this.getSplitLayout(this.mainBottomPanel);
        if (!layout) return;
        const handles = layout.handles;
        if (handles.length === 0) return;

        const totalH = this.mainBottomPanel.node.offsetHeight;
        const targetPos = totalH - targetSize;
        layout.moveHandle(0, targetPos);
    }

    private _setBottomResizeHandleEnabled(enabled: boolean): void {
        if (!this.mainBottomPanel) return;
        const layout = this.getSplitLayout(this.mainBottomPanel);
        if (!layout) return;
        const handles = layout.handles;
        if (handles.length > 0) {
            handles[0].style.pointerEvents = enabled ? '' : 'none';
        }
    }

    private getSplitLayout(panel: TheiaSplitPanel): SplitLayout | undefined {
        const layout = panel.layout;
        if (layout instanceof SplitLayout) {
            return layout;
        }
        return undefined;
    }

    showTopPanelWidget(): void {
        this.topPanel.show();
    }

    protected restrictCrossPanelDragging(): void {
        type PatchableProto = {
            prototype: { _showOverlay(clientX: number, clientY: number): string };
        };
        const PanelClass = TheiaDockPanel as unknown as PatchableProto;
        if ((PanelClass.prototype._showOverlay as { __lvgl_patched?: boolean }).__lvgl_patched) {
            return;
        }
        // Disable widget-to-widget docking overlays in this shell topology.
        PanelClass.prototype._showOverlay = function (
            this: InstanceType<typeof TheiaDockPanel>,
            clientX: number,
            clientY: number
        ): string {
            clientX;
            clientY;
            return 'invalid';
        };
        (PanelClass.prototype._showOverlay as { __lvgl_patched?: boolean }).__lvgl_patched = true;
    }
}

export function initApplicationShell({ rebind }: { rebind: interfaces.Rebind }): void {
    rebind(TheiaApplicationShell).to(AppShell).inSingletonScope();
}

export function showTheiaTopPanel(shell: TheiaApplicationShell): void {
    (shell as AppShell).showTopPanelWidget();
}
